// Color helpers for cosmetics (§7.3): the auto-derived pattern secondary and small HSL tweaks.
// All HSL math is done in sRGB, where "lighter" and "hue +30°" mean what an artist expects.
import { Color, SRGBColorSpace, type ColorRepresentation } from 'three';

const hsl = { h: 0, s: 0, l: 0 };

function wrap01(v: number): number {
  return v - Math.floor(v);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Copies `c` shifted in HSL (sRGB) into `out`. */
export function shiftHsl(out: Color, c: ColorRepresentation, dh: number, ds: number, dl: number): Color {
  out.set(c);
  out.getHSL(hsl, SRGBColorSpace);
  return out.setHSL(wrap01(hsl.h + dh), clamp01(hsl.s + ds), clamp01(hsl.l + dl), SRGBColorSpace);
}

/** Pattern ids (catalog order, §7.3). */
export const PATTERN = { SOLID: 0, STRIPES: 1, POLKA: 2, CHECKER: 3, GRADIENT: 4, GALAXY: 5, CAMO: 6, TIE_DYE: 7 } as const;

/**
 * The pattern's secondary color, derived from the body color (§7.3 "each uses the body color plus
 * an auto-derived secondary"): light bodies get a deeper shade, dark bodies a lighter one, and the
 * painterly patterns shift hue.
 */
export function patternSecondary(out: Color, body: ColorRepresentation, pattern: number): Color {
  out.set(body);
  out.getHSL(hsl, SRGBColorSpace);
  const { h, s, l } = hsl;
  switch (pattern) {
    case PATTERN.GRADIENT:
      return out.setHSL(wrap01(h + 0.09), clamp01(s * 1.05 + 0.05), clamp01(l > 0.55 ? l - 0.1 : l + 0.16), SRGBColorSpace);
    case PATTERN.GALAXY:
      return out.setHSL(wrap01(h + 0.18), clamp01(0.6 + s * 0.35), 0.58, SRGBColorSpace);
    case PATTERN.CAMO:
      // Dark bodies get light blotches, light bodies dark ones; always a clear step apart.
      return out.setHSL(wrap01(h - 0.04), clamp01(s * 0.85), l < 0.45 ? clamp01(l + 0.26) : clamp01(l * 0.55), SRGBColorSpace);
    case PATTERN.TIE_DYE:
      return out.setHSL(wrap01(h + 0.5), clamp01(0.55 + s * 0.4), clamp01(0.5 + (l - 0.5) * 0.4), SRGBColorSpace);
    default:
      if (l >= 0.62) return out.setHSL(wrap01(h - 0.015), clamp01(s + 0.12), clamp01(l - 0.26), SRGBColorSpace);
      if (l < 0.42) return out.setHSL(wrap01(h + 0.02), clamp01(s * 0.9), clamp01(l + 0.3), SRGBColorSpace);
      return out.setHSL(wrap01(h + 0.02), clamp01(s * 0.75), clamp01(l + 0.28), SRGBColorSpace);
  }
}

/** Packs an sRGB hex color into one float that survives a Float32 vertex attribute exactly. */
export function packColor(c: ColorRepresentation): number {
  return new Color(c).getHex(SRGBColorSpace);
}
